<template>
  <v-container class="pa-0 ma-0 checkpoint-selector-container">
    <div v-if="!canvases.length" class="pb-4 pa-2 ma-0" :class="{ 'h-100': !canvases.length }">
      <v-alert variant="tonal" class="mb-1">
        {{ tkey('assortmentsMissingMessage') }}
      </v-alert>
    </div>

    <div v-if="canvases.length" class="h-100 flex-grow-1 pa-2 ma-0">
      <AgGridVue
        v-if="rowData"
        class="w-100 h-100"
        :column-defs="columnDefs"
        :row-data="rowData"
        :grid-options="gridOptions"
        :stop-editing-when-cells-loses-focus="true"
        @cell-value-changed="trackDiff"
        @grid-ready="onGridReady"
      />
    </div>
    <PageActions
      :has-data-changes="hasDataChanges"
      :has-data-errors="hasDataErrors"
      :is-discard-enabled="!isEditingDisabled"
      :save-disabled="isEditingDisabled"
      @discard="discardChanges"
      @save="saveChanges"
    />

    <DependencyTreeFeedbackModal
      :value="dependencyTreeModalOpen"
      :results="dependencyTreeFeedback"
      page="checkpointSelector"
      @close="closeDependencyTreeModal"
      @commit="saveChanges(true)"
    />
  </v-container>
</template>

<script>
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useClusteringStore } from '@/stores/clustering';
import { useDependencyTreeStore } from '@/stores/dependency-tree';
import { useScenariosStore } from '@/stores/scenarios';
import { useExtractsStore } from '@/stores/extracts';
import { useStoreExecutionsStore } from '@/stores/store-executions';
import agGridUtils from '@/utils/ag-grid-utils';
import {
  cloneDeep,
  concat,
  each,
  filter,
  get,
  groupBy,
  isEmpty,
  isNull,
  keyBy,
  map,
  merge,
  orderBy,
  padStart,
  partition,
  pick,
  reduce,
  size,
  sortBy,
} from 'lodash';
import { mapActions, mapState } from 'pinia';

export default {
  setup() {
    const { canEditStoreExecutionPlanning } = usePermission();
    const { t, tkey } = useInternationalization(
      'extract.reports.storeExecutionPlanning.checkpointSelector'
    );
    return { canEditStoreExecutionPlanning, t, tkey };
  },
  data() {
    return {
      gridApi: null,
      columnDefs: [
        {
          headerName: this.tkey('tableHeaders.cluster'),
          field: 'clusterName',
          colId: 'clusterName',
        },
        {
          headerName: this.tkey('tableHeaders.storeClass'),
          field: 'storeClassName',
          colId: 'storeClassName',
        },
        {
          headerName: this.tkey('tableHeaders.sequentialKey'),
          field: 'sequentialKey',
          colId: 'sequentialKey',
          // AG Grid v33 infers cellDataType from the first row. Header rows use numeric sequentialKey
          // values (1, 2) so the column gets inferred as `number`, causing data rows with string
          // values like 'CS01' to render as "Invalid Number". Disabling inference fixes this.
          cellDataType: false,
        },
        {
          headerName: this.tkey('tableHeaders.checkpoint'),
          field: 'checkpointId',
          colId: 'checkpointId',
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: params => {
            const { clusterId, storeClassId } = params.node.data;
            return {
              values: this.getCheckpoints(clusterId, storeClassId),
              field: 'checkpointId',
            };
          },
          valueFormatter: this.formatCheckpointName,
          filterParams: { valueFormatter: this.formatCheckpointName },
          editable: () => !this.isEditingDisabled,
        },
      ],
      gridOptions: {
        columnMenu: 'legacy',
        headerHeight: 40,
        rowHeight: 40,
        singleClickEdit: true,
        suppressContextMenu: true,
        defaultColDef: {
          filter: false,
          sortable: true,
          resizable: true,
          minWidth: 80,
          comparator: agGridUtils.sortings.naturalSort,
          editable: false,
          suppressHeaderMenuButton: true,
          suppressMovable: true,
          flex: 1,
          required: true,
          cellClassRules: {
            'diff-background': this.hasDiff,
            'invalid-nonempty': agGridUtils.validations.validateValueExists,
          },
        },
        getRowId(row) {
          const { data } = row;
          return String(data.sequentialKey);
        },
        isFullWidthRow: params => params.rowNode.data.heading,
        fullWidthCellRenderer: agGridUtils.utils.fullWidthHeadingRenderer,
        postSortRows: this.postSort,
      },
      rowData: null,
      currentStateDiff: {},
      savedState: {},
      dependencyTreeModalOpen: false,
      dependencyTreeFeedback: {},
    };
  },

  computed: {
    ...mapState(useAssortmentCanvasStore, [
      'scenarioCheckpoints',
      'scenarioCheckpointsById',
      'canvases',
    ]),
    ...mapState(useScenariosStore, ['selectedScenario']),
    ...mapState(useClusteringStore, ['selectedClusterScheme', 'noSelectedClusterScheme']),

    hasDataChanges() {
      return !isEmpty(this.currentStateDiff);
    },

    hasDataErrors() {
      // Required for <page-actions> component
      return false;
    },

    forecastCheckpointsByKey() {
      // Only checkpoints which have been forecast are valid here
      // Otherwise the store executions fails as it uses the forecast data.
      const forecastCheckpoints = filter(this.scenarioCheckpoints, { hasBeenForecast: true });

      return groupBy(forecastCheckpoints, sc => `${sc.clusterId}-${sc.storeClassId}`);
    },
    isEditingDisabled() {
      return !this.canEditStoreExecutionPlanning;
    },
  },

  async created() {
    await this.init();
  },

  methods: {
    ...mapActions(useExtractsStore, ['setSelectedStep']),
    ...mapActions(useClusteringStore, ['fetchScenarioClusters']),
    ...mapActions(useStoreExecutionsStore, [
      'fetchCheckpointSelections',
      'saveCheckpointSelections',
    ]),
    ...mapActions(useDependencyTreeStore, ['triggerDependencyTree']),
    ...mapActions(useAssortmentCanvasStore, ['fetchScenarioCheckpoints']),

    onGridReady(params) {
      this.gridApi = params.api;
    },

    addSequentialKeys(array) {
      // Unclustered checkpoints have a null checkpointId
      // These should come first, so we separate them, sort individually then concat
      const [unclustered, clustered] = partition(array, cs => isNull(cs.clusterId));

      const sortedUnclustered = sortBy(unclustered, ['clusterId', 'storeClassId']);
      const sortedClustered = sortBy(clustered, ['clusterId', 'storeClassId']);

      const prefix = this.t('general.keyPrefixes.checkpointSelection');
      const allSelections = [...sortedUnclustered, ...sortedClustered];
      each(allSelections, (val, ix) => {
        val.sequentialKey = `${prefix}${padStart(ix + 1, 2, 0)}`;
      });
    },

    async init() {
      this.setSelectedStep('checkpoint-selector');

      const [checkpointSelections] = await Promise.all([
        this.fetchCheckpointSelections(),
        this.fetchScenarioCheckpoints(),
        this.fetchScenarioClusters(),
      ]);
      const existingSelections = this.formatCheckpointSelections(checkpointSelections);

      const rows = map(this.canvases, c => {
        const { clusterId, storeClassId, storeClassName } = c;
        const clusterName = c.clusterName || this.tkey('unclustered');

        // Use existing checkpoint selections in table data when available
        const checkpoint = get(existingSelections, [clusterId, storeClassId]);

        return {
          id: c._id,
          clusterId,
          clusterName,
          storeClassId,
          storeClassName,
          sequentialKey: get(checkpoint, 'sequentialKey'),
          checkpointId: get(checkpoint, 'checkpointId', null),
        };
      });

      // If no data exists, no sequential keys exist. Calculate them.
      const selectionsWithKey = filter(existingSelections, 'sequentialKey');
      if (isEmpty(selectionsWithKey)) this.addSequentialKeys(rows);

      if (size(rows)) {
        // Insert headers before and after unclustered rows
        // Add sequentialKey for nodeId
        const unclusteredHeader = {
          heading: this.tkey('unclusteredHeading'),
          sequentialKey: 1,
        };
        const clusteredHeader = {
          heading: get(this.selectedClusterScheme, 'name'),
          sequentialKey: 2,
        };

        if (this.noSelectedClusterScheme) {
          this.rowData = concat(unclusteredHeader, rows);
        } else {
          const [clustered, unclustered] = partition(rows, r => r.clusterId);
          this.rowData = concat(unclusteredHeader, unclustered, clusteredHeader, clustered);
        }
      }

      // Backup data
      this.currentStateDiff = {};
      this.$options.rowData = cloneDeep(this.rowData);
      this.savedState = this.formatCheckpointSelections(cloneDeep(rows));
    },

    trackDiff(params) {
      const { clusterId, storeClassId } = params.data;
      const { field } = params.colDef;

      this.updateDiff(clusterId, storeClassId, field, params.value);
    },

    updateDiff(clusterId, storeClassId, field, currentValue) {
      const originalValue = get(this.savedState, [clusterId, storeClassId, field]);
      const existingValue = get(this.currentStateDiff, [clusterId, storeClassId]);

      // If the current state does not match the original, track the state difference
      if (currentValue !== originalValue) {
        if (!this.currentStateDiff[clusterId]) {
          this.currentStateDiff[clusterId] = {};
        }
        if (!this.currentStateDiff[clusterId][storeClassId]) {
          this.currentStateDiff[clusterId][storeClassId] = {};
        }
        this.currentStateDiff[clusterId][storeClassId][field] = currentValue;
      } else if (existingValue) {
        delete this.currentStateDiff[clusterId][storeClassId][field];

        // If there is nothing left in the object, remove it
        if (isEmpty(this.currentStateDiff[clusterId][storeClassId])) {
          delete this.currentStateDiff[clusterId][storeClassId];
        }

        if (isEmpty(this.currentStateDiff[clusterId])) {
          delete this.currentStateDiff[clusterId];
        }
      }
    },

    hasDiff(params) {
      const { clusterId, storeClassId } = params.data;
      const { field } = params.colDef;

      const currentValue = params.value;
      const originalValue = get(this.savedState, [clusterId, storeClassId, field]);

      return agGridUtils.comparators.didValueChange(currentValue, originalValue);
    },

    async saveChanges(commit = false) {
      const updatedCheckpointSelections = merge({}, this.savedState, this.currentStateDiff);

      const updates = reduce(
        updatedCheckpointSelections,
        (acc, selection) => {
          each(Object.keys(selection), storeClassId => {
            acc.push({
              ...pick(selection[storeClassId], ['clusterId', 'sequentialKey', 'checkpointId']),
              storeClassId,
            });
          });
          return acc;
        },
        []
      );

      // Trigger dependency tree
      const results = await this.triggerDependencyTree({
        params: { change: 'checkpointSelectorModified', updates: {}, commit },
      });

      if (results.needsFeedback) {
        this.dependencyTreeFeedback = results.output;
        this.dependencyTreeModalOpen = true;
        return;
      }

      await this.saveCheckpointSelections(updates);

      // Update backup data with the saved changes
      this.$options.rowData = cloneDeep(this.rowData);
      this.savedState = updatedCheckpointSelections;

      // Get the modified row nodes
      const modifiedRows = reduce(
        this.currentStateDiff,
        (acc, selection, clusterId) => {
          each(Object.keys(selection), key => {
            const sequentialKey = get(this.savedState, [clusterId, key, 'sequentialKey']);
            acc.push(this.gridApi.getRowNode(sequentialKey));
          });
          return acc;
        },
        []
      );
      // Refresh diff cell colouring
      this.gridApi.redrawRows({ rowNodes: modifiedRows });
      // Reset diff
      this.currentStateDiff = {};
    },

    discardChanges() {
      this.rowData = cloneDeep(this.$options.rowData);
      this.currentStateDiff = {};
    },

    postSort({ nodes }) {
      // Runs before onGridReady, check gridApi
      if (isEmpty(nodes) || isEmpty(this.gridApi)) return;

      // Separate rows with clusterIds
      const [clustered, unclustered] = partition(nodes, s => s.data.clusterId);
      // Separate the headers from unclustered rows
      const [headings, unclusteredRows] = partition(unclustered, s => s.data.heading);
      const sortedHeadings = orderBy(headings, [n => n.data.sequentialKey]);

      const dataRows = [unclusteredRows, clustered];
      const orderedNodes = reduce(
        sortedHeadings,
        (acc, heading, index) => {
          return concat(acc, heading, dataRows[index]);
        },
        []
      );

      // Have to use splice to update reference to nodes. see https://www.ag-grid.com/javascript-grid-sorting/#post-sort
      nodes.splice(0, nodes.length, ...orderedNodes);

      // Need to redraw rows to get correct styling for groups
      this.gridApi.redrawRows({ nodes });
      return nodes;
    },

    getCheckpoints(clusterId, storeClassId) {
      const key = `${clusterId}-${storeClassId}`;
      const checkpointsForCanvas = this.forecastCheckpointsByKey[key];
      return map(checkpointsForCanvas, '_id');
    },

    formatCheckpointSelections(data) {
      const clusteredSelections = groupBy(data, 'clusterId');

      return reduce(
        clusteredSelections,
        (acc, selections, index) => {
          acc[index] = keyBy(selections, 'storeClassId');
          return acc;
        },
        {}
      );
    },

    formatCheckpointName(params) {
      // Text to display when no selection has been made
      if (!params.value) return this.tkey('checkpointSelect');
      return get(this.scenarioCheckpointsById[params.value], 'checkpointMeta.name');
    },

    closeDependencyTreeModal() {
      this.dependencyTreeModalOpen = false;
      this.dependencyTreeFeedback = {};
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.checkpoint-selector-container {
  flex: 1;
  max-width: 100%;
  height: 61vh; /* To fit layout */
}

:deep(.v-alert) {
  background: $assortment-warning-alert-background !important;
  border: 1px solid $assortment-warning-alert-border !important;
  border-radius: 0 !important;
  padding: 14px;

  &:before {
    content: none;
  }

  .v-alert__content {
    color: $assortment-text-colour;
    font-size: 1.2rem;
    line-height: 1.5rem;
  }
}

:deep(.ag-theme-alpine) {
  .ag-row {
    color: $assortment-table-textfield-colour;
  }

  .ag-header-row {
    color: $assortment-text-colour;

    &:not(.ag-header-row-column-group) {
      border-top: 1px solid $assortment-divider-colour;
    }
  }

  .ag-header-group-text {
    color: $assortment-table-header-colour;
    font-size: 1.4rem;
    font-weight: 600;
    line-height: 1.6rem;
  }

  .ag-full-width-row {
    align-items: center;
    background-color: $assortment-table-white-bg-colour;
    border-bottom: 1px solid $assortment-ag-grid-header-separator-colour;
    display: flex;
    padding: 0 5px;
  }
}
</style>
